import React, { createContext, useContext } from 'react';

import { OcChatConfig, PreviewState } from '../../../types/oc';
import { useOcFabWidgetOptions } from '../contexts/OcFabWidgetOptionsContext';
import { SessionManager, usePreviewSessionManager, useSessionManager } from './useOcSessionManager';

const SessionManagerContext = createContext<SessionManager | null>(null);
SessionManagerContext.displayName = 'SessionManagerContext';

export const useSessionManagerContext = (): SessionManager => {
    const context = useContext(SessionManagerContext);
    if (!context) {
        throw new Error('useSessionManagerContext must be used within SessionManagerProvider');
    }
    return context;
};

export interface SessionManagerProviderProps {
    children: React.ReactNode;
    config: OcChatConfig;
    originToolId: string;
    isAuthenticated: boolean;
    isPublicAnonymousUser?: boolean;
    userId: string;
    previewState?: PreviewState;
}

const PreviewProvider: React.FC<{
    children: React.ReactNode;
    previewState: PreviewState;
}> = ({ children, previewState }) => {
    const { showChatHistory } = useOcFabWidgetOptions();
    const sessionManager = usePreviewSessionManager(showChatHistory, previewState);
    return <SessionManagerContext.Provider value={sessionManager}>{children}</SessionManagerContext.Provider>;
};

const ProductionProvider: React.FC<{
    children: React.ReactNode;
    config: OcChatConfig;
    originToolId: string;
    isAuthenticated: boolean;
    isPublicAnonymousUser: boolean;
    userId: string;
}> = ({ children, config, originToolId, isAuthenticated, isPublicAnonymousUser, userId }) => {
    const { showChatHistory } = useOcFabWidgetOptions();
    const sessionManager = useSessionManager(
        showChatHistory,
        config,
        originToolId,
        isAuthenticated,
        isPublicAnonymousUser,
        userId,
    );
    return <SessionManagerContext.Provider value={sessionManager}>{children}</SessionManagerContext.Provider>;
};

export const SessionManagerProvider: React.FC<SessionManagerProviderProps> = ({
    children,
    config,
    originToolId,
    isAuthenticated,
    isPublicAnonymousUser = false,
    userId,
    previewState,
}) => {
    if (config.mode === 'preview' && previewState) {
        return <PreviewProvider previewState={previewState}>{children}</PreviewProvider>;
    }
    return (
        <ProductionProvider
            config={config}
            originToolId={originToolId}
            isAuthenticated={isAuthenticated}
            isPublicAnonymousUser={isPublicAnonymousUser}
            userId={userId}
        >
            {children}
        </ProductionProvider>
    );
};

